// Release A — Monthly Box admin (Waitlist + Complimentary eligibility).
//
// Phase 2 will add Subscribers + Billing status + Fulfillment tabs here.
// For now this ships the two already-live surfaces: waitlist signups
// and admin-granted complimentary entitlements.
import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Users, Gift, Search, Plus, Save, Trash2 } from "lucide-react";
import { SubscribersTab, FulfillmentTab, TermsTab } from "@/components/monthlybox/AdminBoxTabs";
import api, { apiErrorMessage } from "@/lib/api";

export default function AdminMonthlyBox() {
  const [tab, setTab] = useState("waitlist");
  return (
    <div className="p-4 lg:p-6 min-h-screen bg-[#06080b] text-white" data-testid="admin-monthly-box">
      <h1 className="font-anton uppercase text-2xl mb-5 flex items-center gap-2">
        <Gift className="h-6 w-6 text-lifted-green" /> Monthly Box Operations
      </h1>
      <div className="flex gap-2 mb-5 border-b border-white/10 pb-0">
        <TabBtn active={tab === "waitlist"} onClick={() => setTab("waitlist")} testid="tab-waitlist">Waitlist</TabBtn>
        <TabBtn active={tab === "complimentary"} onClick={() => setTab("complimentary")} testid="tab-complimentary">Complimentary</TabBtn>
        <TabBtn active={tab === "subscribers"} onClick={() => setTab("subscribers")} testid="tab-subscribers">Subscribers</TabBtn>
        <TabBtn active={tab === "fulfillment"} onClick={() => setTab("fulfillment")} testid="tab-fulfillment">Fulfillment</TabBtn>
        <TabBtn active={tab === "terms"} onClick={() => setTab("terms")} testid="tab-terms">Terms & Billing Setup</TabBtn>
      </div>
      {tab === "waitlist" && <WaitlistTab />}
      {tab === "complimentary" && <ComplimentaryTab />}
      {tab === "subscribers" && <SubscribersTab />}
      {tab === "fulfillment" && <FulfillmentTab />}
      {tab === "terms" && <TermsTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 font-bebas uppercase tracking-[0.18em] text-xs border-b-2 ${
        active ? "border-lifted-green text-lifted-green" : "border-transparent text-white/60 hover:text-white"
      }`}>{children}</button>
  );
}

function WaitlistTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/monthly-box/waitlist");
      setRows(data.waitlist || []);
    } catch (e) { setErr(apiErrorMessage(e, "Could not load waitlist")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const filtered = q
    ? rows.filter((r) => (r.email + " " + (r.name || "")).toLowerCase().includes(q.toLowerCase()))
    : rows;
  return (
    <div data-testid="waitlist-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="h-3 w-3 absolute left-3 top-3 text-white/50" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            className="input-lifted w-full pl-8" placeholder="Search email or name…"
            data-testid="waitlist-search" />
        </div>
        <span className="text-xs text-white/50 ml-auto">{filtered.length} of {rows.length}</span>
      </div>
      {err && <div className="glass-panel p-3 mb-3 border border-rose-500/40 text-rose-200 text-sm">{err}</div>}
      {loading ? <Loader2 className="h-4 w-4 animate-spin text-white/50" /> : (
        <div className="glass-panel overflow-hidden">
          <table className="w-full text-xs" data-testid="waitlist-table">
            <thead className="bg-black/40 text-white/60">
              <tr>
                <th className="text-left px-3 py-2">Joined</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Plan</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-white/50 py-10" data-testid="waitlist-empty">
                  No signups yet.
                </td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`waitlist-row-${r.id}`}>
                  <td className="px-3 py-2 text-white/60">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-white">{r.email}</td>
                  <td className="px-3 py-2 text-white/70">{r.name || "—"}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded border border-lifted-green/40 text-lifted-green text-[10px] font-bebas uppercase tracking-wider">
                      {r.plan_id}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ComplimentaryTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [adding, setAdding] = useState(false);
  const [userId, setUserId] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/monthly-box/complimentary");
      setRows(data.records || []);
    } catch (e) { setErr(apiErrorMessage(e, "Could not load records")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function grant() {
    if (!userId) return;
    try {
      await api.post("/admin/monthly-box/eligibility", {
        user_id: userId, complimentary: true, note: note || "",
      });
      setUserId(""); setNote(""); setAdding(false);
      await load();
    } catch (e) { setErr(apiErrorMessage(e, "Grant failed")); }
  }
  async function revoke(uid) {
    if (!window.confirm("Revoke complimentary status for this user?")) return;
    try {
      await api.post("/admin/monthly-box/eligibility", {
        user_id: uid, complimentary: false,
      });
      await load();
    } catch (e) { setErr(apiErrorMessage(e, "Revoke failed")); }
  }

  return (
    <div data-testid="complimentary-panel">
      <div className="flex items-center gap-3 mb-4">
        <Users className="h-4 w-4 text-lifted-green" />
        <span className="text-xs text-white/70">Admin-controlled complimentary entitlements</span>
        <button onClick={() => setAdding((v) => !v)} data-testid="grant-toggle"
          className="btn-ghost ml-auto text-xs inline-flex items-center gap-1">
          <Plus className="h-3 w-3" /> Grant
        </button>
      </div>
      {adding && (
        <div className="glass-panel p-4 mb-4" data-testid="grant-form">
          <div className="grid md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
            <div>
              <label className="block text-[10px] font-bebas uppercase tracking-wider text-white/70 mb-1">User ID</label>
              <input value={userId} onChange={(e) => setUserId(e.target.value)}
                className="input-lifted w-full" placeholder="user_id…"
                data-testid="grant-user-id" />
            </div>
            <div>
              <label className="block text-[10px] font-bebas uppercase tracking-wider text-white/70 mb-1">Note (internal)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                className="input-lifted w-full" data-testid="grant-note" />
            </div>
            <button onClick={grant} className="btn-lifted" data-testid="grant-save">
              <Save className="h-3 w-3 inline mr-1" /> Grant
            </button>
          </div>
        </div>
      )}
      {err && <div className="glass-panel p-3 mb-3 border border-rose-500/40 text-rose-200 text-sm">{err}</div>}
      {loading ? <Loader2 className="h-4 w-4 animate-spin text-white/50" /> : (
        <div className="glass-panel overflow-hidden">
          <table className="w-full text-xs" data-testid="complimentary-table">
            <thead className="bg-black/40 text-white/60">
              <tr>
                <th className="text-left px-3 py-2">User ID</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Note</th>
                <th className="text-left px-3 py-2">Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.filter((r) => r.complimentary).length === 0 ? (
                <tr><td colSpan={5} className="text-center text-white/50 py-10" data-testid="complimentary-empty">
                  No complimentary members yet.
                </td></tr>
              ) : rows.filter((r) => r.complimentary).map((r) => (
                <tr key={r.user_id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-3 py-2 text-white font-mono text-[11px]">{r.user_id}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded border border-lifted-green/40 text-lifted-green text-[10px] font-bebas uppercase tracking-wider">
                      Active
                    </span>
                  </td>
                  <td className="px-3 py-2 text-white/70">{r.note || "—"}</td>
                  <td className="px-3 py-2 text-white/50">{(r.updated_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2">
                    <button onClick={() => revoke(r.user_id)}
                      data-testid={`revoke-${r.user_id}`}
                      className="text-rose-300 hover:text-rose-200 text-[11px] inline-flex items-center gap-1">
                      <Trash2 className="h-3 w-3" /> Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
